import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useMemo } from "react";

import {
  isChatPath,
  isSettingsPath,
  pathMatchesRoute,
  Route,
  getSidebarNavGroups,
} from "@/config/navigation";
import { ChatNavContent } from "@/components/chat/ChatNavContent";
import { SettingsNavContent } from "@/components/settings/SettingsNavContent";
import { useAccount } from "@/hooks/useAccount";
import { useAuth } from "@/providers/auth.provider";
import { UserMenu } from "./UserMenu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@gogive/design-system";
import { GlobalSearchInput } from "@/components/GlobalSearchInput";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarHeaderMenu,
  SidebarHeaderRow,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarScrollArea,
  useSidebar,
} from "@gogive/design-system/components/sidebar";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface NavItemProps {
  name: string;
  href: string;
  Icon: LucideIcon;
  childrenRoutes?: Route[];
  nested?: boolean;
  disabled?: boolean;
  disabledTitle?: string;
}

function NavItem({
  name,
  href,
  Icon,
  childrenRoutes,
  nested = false,
  disabled = false,
  disabledTitle = "Coming soon",
}: NavItemProps) {
  const pathname = usePathname() ?? "/";
  const normalizedPath = pathname.replace(/\/+$/, "") || "/";
  const normalizedHref = href.replace(/\/+$/, "") || "/";

  const active =
    !disabled &&
    ((href.includes("[") && pathMatchesRoute(href, pathname)) ||
      normalizedPath === normalizedHref ||
      (normalizedHref !== "/" &&
        normalizedPath.startsWith(`${normalizedHref}/`)));

  const visibleChildren = useMemo(
    () => (childrenRoutes ?? []).filter((c) => !c.hidden),
    [childrenRoutes],
  );

  const hasChildren = visibleChildren.length > 0;

  const hasActiveChildren =
    hasChildren &&
    childrenRoutes?.some((r) => pathMatchesRoute(r.path, pathname));

  const expand = useMemo(
    () => Boolean(hasActiveChildren || (hasChildren && active)),
    [active, hasActiveChildren, hasChildren],
  );

  if (disabled) {
    const disabledLabel = (
      <>
        <Icon />
        <span>{name}</span>
      </>
    );

    if (nested) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex w-full">
              <SidebarMenuSubButton
                asChild
                size="md"
                className="pointer-events-none opacity-60"
              >
                <span aria-disabled="true">{disabledLabel}</span>
              </SidebarMenuSubButton>
            </span>
          </TooltipTrigger>
          <TooltipContent side="right">{disabledTitle}</TooltipContent>
        </Tooltip>
      );
    }

    return (
      <SidebarMenuItem>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex w-full">
              <SidebarMenuButton
                disabled
                aria-disabled
                className="w-full opacity-60"
              >
                {disabledLabel}
              </SidebarMenuButton>
            </span>
          </TooltipTrigger>
          <TooltipContent side="right">{disabledTitle}</TooltipContent>
        </Tooltip>
      </SidebarMenuItem>
    );
  }

  if (nested) {
    if (hasChildren) {
      return (
        <>
          <SidebarMenuSubButton asChild isActive={active} size="md">
            <Link href={href}>
              <Icon />
              <span>{name}</span>
            </Link>
          </SidebarMenuSubButton>
          {expand && (
            <SidebarMenuSub>
              {visibleChildren.map((r) => (
                <SidebarMenuSubItem key={r.path}>
                  <NavItem
                    name={r.name}
                    href={r.path}
                    Icon={r.icon}
                    childrenRoutes={r.children}
                    nested
                    disabled={r.disabled}
                    disabledTitle={r.disabledTitle}
                  />
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          )}
        </>
      );
    }

    return (
      <SidebarMenuSubButton asChild isActive={active} size="md">
        <Link href={href}>
          <Icon />
          <span>{name}</span>
        </Link>
      </SidebarMenuSubButton>
    );
  }

  if (hasChildren) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton asChild isActive={active}>
          <Link href={href}>
            <Icon />
            <span>{name}</span>
          </Link>
        </SidebarMenuButton>
        {expand && (
          <SidebarMenuSub>
            {visibleChildren.map((r) => (
              <SidebarMenuSubItem key={r.path}>
                <NavItem
                  name={r.name}
                  href={r.path}
                  Icon={r.icon}
                  childrenRoutes={r.children}
                  nested
                  disabled={r.disabled}
                  disabledTitle={r.disabledTitle}
                />
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        )}
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={active}>
        <Link href={href}>
          <Icon />
          <span>{name}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function SidebarBody({ isAdmin }: { isAdmin: boolean }) {
  const { account, isLoading } = useAccount();
  const stripeConnected = !isLoading && !!account?.stripeConnected;
  const navGroups = useMemo(() => getSidebarNavGroups(isAdmin), [isAdmin]);

  return (
    <>
      <SidebarHeader>
        <SidebarHeaderRow>
          <SidebarHeaderMenu>
            <SidebarMenuItem>
              <UserMenu placement="sidebar" className="w-full" />
            </SidebarMenuItem>
          </SidebarHeaderMenu>
        </SidebarHeaderRow>
        {stripeConnected ? <GlobalSearchInput variant="sidebar" /> : null}
      </SidebarHeader>
      <SidebarContent className="min-h-0 flex-1 gap-0 overflow-hidden px-0 py-0">
        <SidebarScrollArea className="px-0">
          {navGroups.map((group) => (
            <SidebarGroup key={group.id}>
              <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {group.routes.map((r: Route) => (
                    <NavItem
                      key={r.path}
                      name={r.name}
                      href={r.path}
                      Icon={r.icon}
                      childrenRoutes={r.children}
                      disabled={r.disabled}
                      disabledTitle={r.disabledTitle}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </SidebarScrollArea>
      </SidebarContent>
    </>
  );
}

const SideNav = () => {
  const { user } = useAuth();
  const pathname = usePathname() ?? "/";
  const { openMobile, setOpenMobile } = useSidebar();
  const isAdmin = useMemo(
    () => (!user ? false : ["SUPER_ADMIN", "ADMIN"].includes(user.role)),
    [user],
  );
  const inSettings = isSettingsPath(pathname);
  const inChat = isChatPath(pathname);

  useEffect(() => {
    setOpenMobile(false);
  }, [pathname, setOpenMobile]);

  const sidebarSurfaceClass =
    "bg-card text-sidebar-foreground [--sidebar:var(--card)]";
  const sidebarClassName = cn(
    "hidden h-svh w-(--sidebar-width) min-w-(--sidebar-width) shrink-0 border-r border-sidebar-border md:flex",
    sidebarSurfaceClass,
  );

  const sidebarBody = inSettings ? (
    <SettingsNavContent isAdmin={isAdmin} />
  ) : inChat ? (
    <ChatNavContent />
  ) : (
    <SidebarBody isAdmin={isAdmin} />
  );

  return (
    <>
      <Sidebar collapsible="none" variant="floating" className={sidebarClassName}>
        {sidebarBody}
      </Sidebar>
      <Sheet open={openMobile} onOpenChange={setOpenMobile}>
        <SheetContent
          side="left"
          className={cn(
            "w-(--sidebar-width) gap-0 border-r border-sidebar-border p-0",
            sidebarSurfaceClass,
          )}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Navigation</SheetTitle>
            <SheetDescription>Charity portal navigation</SheetDescription>
          </SheetHeader>
          <div className="flex h-full min-h-0 flex-col">
            {sidebarBody}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default SideNav;
